/**
 * Error Messages Utility
 * Provides error message configurations for the error reporting system.
 *
 * All strings are wrapped in i18next.t() so they are picked up by `npm run localize`.
 *
 * USAGE:
 *   // In a Pinia store action:
 *   import { reportFatalError, reportNonFatalError } from '@/utils/storeErrorReporting';
 *   import { getOrgSaveError } from '@/utils/errorMessages';
 *
 *   reportNonFatalError(error, getOrgSaveError());
 *   reportFatalError(error, getAccessDeniedError());
 *
 *   // In a Vue component (via composable):
 *   import { useErrorReporting } from '@/composables/useErrorReporting';
 *   const { reportNonFatalError } = useErrorReporting();
 *
 *   reportNonFatalError(error, getOrgSaveError());
 *
 * ERROR TYPES:
 *   Fatal     — fullscreen Error.vue page, execution stops (auth failure, missing critical data)
 *   Non-fatal — ErrorBanner at top of page, user can retry (form save, data load)
 *   Silent    — console only, no UI change (background sync, analytics)
 */

import i18next from 'i18next';

/**
 * Get general error message
 * @returns {string} General error message
 */
export function getGeneralError() {
    return i18next.t('Oops, something went wrong. Please try again later.');
}

/**
 * Get access denied error configuration (for educators not provisioned)
 * @returns {Object} Error configuration object
 */
export function getAccessDeniedError() {
    return {
        title: i18next.t("Looks like you're trying to access Career Connect"),
        message: i18next.t('You are not yet provisioned to use Career Connect. Click below to learn more about how to get access.'),
        image: 'zoeConcerned',
        primaryButton: {
            text: i18next.t('Learn More'),
            link: '/',
        },
    };
}

/**
 * Get token not found error configuration (for org/volunteers)
 * @returns {Object} Error configuration object
 */
export function getTokenNotFoundError() {
    return {
        title: i18next.t('Error fetching organization'),
        message: i18next.t('No authentication token available'),
        image: 'zoeConcerned',
        primaryButton: {
            text: i18next.t('Back to Homepage'),
            link: '/',
        },
    };
}

/**
 * Get failed org fetch error configuration (fatal - for current user's org)
 * @returns {Object} Error configuration object
 */
export function getFailedOrgFetchError() {
    const preprods = ['development', 'qa', 'staging'];
    const env = import.meta.env.MODE;
    const message = preprods.includes(env)
        ? "We're currently unable to fetch your organization data. Please try again later. NOTE FOR PREPROD: This error can show if you have not granted 'local-network-access' for this page in Chrome. See https://developer.chrome.com/blog/local-network-access for more information."
        : i18next.t("We're currently unable to fetch your organization data. Please try again later.");

    return {
        title: i18next.t('Error fetching organization'),
        message,
        image: 'zoeConcerned',
        primaryButton: {
            text: i18next.t('Back to Homepage'),
            link: '/',
        },
    };
}

/**
 * Get org view error configuration (non-fatal - for viewing any org)
 * @returns {Object} Error configuration object
 */
export function getOrgViewError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get org save error configuration (non-fatal - for saving org data)
 * @returns {Object} Error configuration object
 */
export function getOrgSaveError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get org activate error configuration (non-fatal - for activating org)
 * @returns {Object} Error configuration object
 */
export function getOrgActivateError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get org employees create error configuration (non-fatal - for creating employee invites)
 * @returns {Object} Error configuration object
 */
export function getOrgEmployeesCreateError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get org employee update error configuration (non-fatal - for updating employee)
 * @returns {Object} Error configuration object
 */
export function getOrgEmployeeUpdateError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get org employee remove error configuration (non-fatal - for removing employee)
 * @returns {Object} Error configuration object
 */
export function getOrgEmployeeRemoveError() {
    return {
        title: i18next.t('Failed to delete. Please try again.'),
    };
}

/**
 * Get org avatar update error configuration (non-fatal - for updating avatar)
 * @returns {Object} Error configuration object
 */
export function getOrgAvatarUpdateError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get organizations list error configuration (non-fatal - for fetching orgs list)
 * @returns {Object} Error configuration object
 */
export function getOrganizationsListError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get org employees list error configuration (non-fatal - for fetching employees list)
 * @returns {Object} Error configuration object
 */
export function getOrgEmployeesListError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get industries load error configuration (non-fatal - for fetching industries)
 * @returns {Object} Error configuration object
 */
export function getIndustriesLoadError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get CSV export error configuration (non-fatal - for CSV download failures)
 * @returns {Object} Error configuration object
 */
export function getOrgEmployeesExportError() {
    return {
        title: i18next.t('Failed to export. Please try again.'),
    };
}

/**
 * Get student access denied error configuration
 * @returns {Object} Error configuration object
 */
export function getStudentAccessDeniedError() {
    return {
        title: i18next.t("Looks like you're trying to access Career Connect"),
        message: i18next.t(
            'Career Connect is currently available for teachers only. Please contact your teacher or school administrator for more information.',
        ),
        image: 'zoeConcerned',
    };
}

/**
 * Get authentication failed error configuration
 * @returns {Object} Error configuration object
 */
export function getAuthFailedError() {
    return {
        title: i18next.t('Authentication Failed'),
        message: i18next.t('Oops, something went wrong. Please try again later.'),
        image: 'zoeConcerned',
    };
}

/**
 * Get user data error configuration
 * @returns {Object} Error configuration object
 */
export function getUserDataError() {
    return {
        title: i18next.t('User Data Error'),
        message: i18next.t('Oops, something went wrong. Please try again later.'),
        image: 'zoeConcerned',
    };
}

/**
 * Get user fetch error configuration (non-fatal)
 * @returns {Object} Error configuration object
 */
export function getUserFetchError() {
    return {
        title: i18next.t("We're currently unable to fetch your user data. Please try again later."),
    };
}

/**
 * Get request update error configuration
 * @returns {Object} Error configuration object
 */
export function getRequestUpdateError() {
    return {
        title: i18next.t('We were unable to update this request. Please try again later.'),
    };
}

/**
 * Get request not found error configuration
 * @returns {Object} Error configuration object
 */
export function getRequestNotFoundError() {
    return {
        title: i18next.t('Request Not Found'),
        message: i18next.t("It looks like this request doesn't exist or was removed."),
        primaryButton: {
            text: i18next.t('Go Back'),
            action: () => window.history.back(),
        },
        image: 'error-404',
    };
}

/**
 * Get request unavailable error configuration (for employees who can't access)
 * @returns {Object} Error configuration object
 */
export function getRequestUnavailableError() {
    return {
        title: i18next.t('Request Details Unavailable'),
        message: i18next.t('This request is no longer available. It may have been filled by another volunteer or removed by the educator.'),
        primaryButton: {
            text: i18next.t('Browse Requests'),
            link: '/connect-directory?1=my-connects',
        },
        image: 'error-404',
    };
}

/**
 * Get request forbidden error configuration (for educators viewing a request they don't own)
 * @returns {Object} Error configuration object
 */
export function getRequestForbiddenError() {
    return {
        title: i18next.t('Permission Denied'),
        message: i18next.t("You don't have permission to view this request. It may belong to a different educator, or it has been deleted."),
        primaryButton: {
            text: i18next.t('Back to Dashboard'),
            link: '/',
        },
        image: 'error-404',
    };
}

/**
 * Returns error for circumstances where editing a draft (while remaining a draft) failed.
 * @returns {Object} Relevant error object
 */
export function getSaveAsDraftFailedError() {
    return {
        title: i18next.t('This draft failed to update'),
        message: i18next.t(
            'This draft failed to update. If this problem regularly happens, create a new request using the contents from this draft.',
        ),
    };
}

/**
 * Get messaging token error configuration (fatal - messaging cannot initialize)
 * @returns {Object} Error configuration object
 */
export function getMessagingTokenError() {
    return {
        title: i18next.t('Messaging Unavailable'),
        message: i18next.t('Unable to initialize messaging. Please refresh the page.'),
    };
}

/**
 * Get load messages error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getLoadMessagesError() {
    return {
        title: i18next.t('Failed to load messages. Please try again.'),
    };
}

/**
 * Get send message error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getSendMessageError() {
    return {
        title: i18next.t('Message failed to send. Please try again.'),
    };
}

/**
 * Get attachment upload error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getAttachmentUploadError() {
    return {
        title: i18next.t('Failed to upload. Please try again.'),
    };
}

/**
 * Get attachment delete error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getAttachmentDeleteError() {
    return {
        title: i18next.t('Failed to delete. Please try again.'),
    };
}

/**
 * Get send request error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getSendRequestError() {
    return {
        title: i18next.t('Unable to send request. Please refresh the page and try again.'),
    };
}

/**
 * Get request context load error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getRequestContextLoadError() {
    return {
        title: i18next.t('Unable to load the CareerShare for this request. Please try again.'),
    };
}

/**
 * Get create message store error configuration (non-fatal - user can retry)
 * @returns {Object} Error configuration object
 */
export function getCreateMessageStoreError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}

/**
 * Get welcome modal slides load error configuration (non-fatal - modal shows error state)
 * @returns {Object} Error configuration object
 */
export function getWelcomeModalSlidesError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get welcome modal dismissal check error configuration (silent - defaults to hiding modal)
 * @returns {Object} Error configuration object
 */
export function getWelcomeModalDismissalCheckError() {
    return {
        title: i18next.t('Failed to load. Please try again.'),
    };
}

/**
 * Get welcome modal dismissal save error configuration (silent - non-blocking)
 * @returns {Object} Error configuration object
 */
export function getWelcomeModalDismissalSaveError() {
    return {
        title: i18next.t('Failed to save. Please try again.'),
    };
}
